import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { formatNumber, formatPercent, NO_DATA_DASH } from '../../lib/format';
import { Icon } from '../Icon';
import { Meter } from './Meter';

/**
 * MEDIDOR DE LOTAÇÃO — o `Meter` com a régua de lotação embutida (D3/G5, 07/10/2026).
 *
 * Diferente do `Meter` puro, aqui EXISTE régua calibrada, e ela é a mesma da barra de
 * lotação do grupo: **a partir de 85% vira `warning`** (dá tempo de abrir outro grupo) e
 * **em 100% vira `danger`** (o grupo/campanha saiu do rodízio). Por isso o tom é deduzido do
 * valor aqui dentro — a régua é do produto, não de cada tela. A cor nunca está sozinha: o
 * ícone de alerta e o texto dizem a faixa (2 canais).
 *
 * Duas formas, a mesma régua (as frentes D3 e G5 nasceram em paralelo e foram unificadas):
 *  - **em linha** (padrão: `value` + `caption`): medidor + percentual ou a frase pronta, para
 *    tabela e linha de campanha;
 *  - **empilhada** (quando `vacancies` é passado — card de campanha): medidor em cima e
 *    "Lotação 84% (quase cheio) · 480 vagas" embaixo.
 *
 * `null` é "sem contagem" (traço + trilho apagado), nunca 0%. O valor chega PRONTO do
 * servidor (invariante 3): o componente não soma nem divide.
 */
export type CapacityTone = 'ok' | 'warning' | 'danger' | 'none';

export const CAPACITY_WARNING = 0.85;

/** A régua, exportada para a tela escrever a frase com a mesma fronteira do desenho. */
export function capacityTone(value: number | null | undefined): CapacityTone {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'none';
  if (value >= 1) return 'danger';
  if (value >= CAPACITY_WARNING) return 'warning';
  return 'ok';
}

const METER_TONE = {
  ok: 'brand',
  warning: 'warning',
  danger: 'danger',
  none: 'neutral',
} as const;

const FAIXA: Record<CapacityTone, string | null> = {
  ok: null,
  warning: 'quase cheio',
  danger: 'lotado',
  none: null,
};

export interface CapacityMeterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Ocupação de 0 a 1 (participantes ÷ teto). `null` = sem contagem. */
  value?: number | null;
  /** Sinônimo de `value` (forma do card de campanha, G5). */
  fraction?: number | null;
  /**
   * Vagas até o teto. Passar este campo (mesmo `null`) escolhe a forma EMPILHADA.
   * `null` = sem contagem.
   */
  vacancies?: number | null;
  /** Para leitor de tela e para a frase (ex.: "Lotação da campanha"). Padrão: "Lotação". */
  label?: string;
  /** A frase do número, ex.: "Lotação 82% · 410 vagas". Sem ela, mostra só o percentual. */
  caption?: ReactNode;
  size?: 'sm' | 'md';
}

export function CapacityMeter({
  value,
  fraction,
  vacancies,
  label = 'Lotação',
  caption,
  size = 'md',
  className,
  ...props
}: CapacityMeterProps) {
  const medido = value !== undefined ? value : fraction;
  const tone = capacityTone(medido);
  const temDado = tone !== 'none' && medido !== null && medido !== undefined;

  if (vacancies !== undefined) {
    const faixa = FAIXA[tone];
    return (
      <div
        className={cn('flex flex-col gap-1', className)}
        data-tone={temDado ? METER_TONE[tone] : 'no-data'}
        {...props}
      >
        <Meter
          value={temDado ? medido : null}
          tone={METER_TONE[tone]}
          segments={10}
          size="sm"
          label={label}
        />
        <p className="nums text-2xs text-muted-foreground">
          {temDado ? (
            <>
              {label}{' '}
              <strong
                className={cn(
                  'font-semibold',
                  tone === 'danger'
                    ? 'text-danger'
                    : tone === 'warning'
                      ? 'text-warning'
                      : 'text-foreground',
                )}
              >
                {formatPercent(medido, 0)}
              </strong>
              {faixa ? ` (${faixa})` : ''}
              {vacancies !== null
                ? ` · ${formatNumber(vacancies)} ${vacancies === 1 ? 'vaga' : 'vagas'}`
                : ''}
            </>
          ) : (
            <>{label} — nenhum grupo contado ainda</>
          )}
        </p>
      </div>
    );
  }

  const pct = temDado ? `${Math.round(Math.min(1, Math.max(0, medido)) * 100)}%` : NO_DATA_DASH;

  return (
    <div className={cn('inline-flex items-center gap-2', className)} {...props}>
      <Meter value={medido} label={label} tone={METER_TONE[tone]} size={size} segments={5} />
      <span
        className={cn(
          'nums inline-flex items-center gap-1 text-xs',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning',
          (tone === 'ok' || tone === 'none') && 'text-muted-foreground',
        )}
      >
        {tone === 'warning' || tone === 'danger' ? (
          <Icon name="triangle-alert" size={12} aria-hidden="true" />
        ) : null}
        {caption ?? pct}
      </span>
    </div>
  );
}
